import React, {
    Children,
    useRef,
    useEffect,
    useCallback,
    useState
} from 'react'
import { Tabs as TabWrapper, TabList, Tab as TabHeader } from 'react-tabs'
import defaultClasses from './tabs.module.scss'
import { mergeClasses } from 'airglobal/pylot-rich-content/src/PageBuilder/classify'
import { arrayOf, number, oneOf, shape, string } from 'prop-types'
import throttle from 'lodash.throttle'

/**
 * Upper case the first letter of a string
 *
 * @param {string} string
 * @returns {string}
 */
const upperCaseString = (string) => {
    if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1)
    }
}

const Tabs = (props) => {
    const classes = mergeClasses(defaultClasses, props.classes)
    const navigationRef = useRef(null)
    const tabRefs = useRef([]) // To store references to each tab panel for scrolling
    const [scrollElement, setScrollElement] = useState(null)
    const [gradient, setGradient] = useState(null)
    const [selectedIndex, setSelectedIndex] = useState(props.defaultIndex || 0) // Default selected tab index
    const isScrolling = useRef(false)
    const lastScrollY = useRef(0)
    const clientX = useRef(0)
    const scrollX = useRef(0)

    const {
        tabNavigationAlignment = 'left',
        minHeight,
        defaultIndex = 0,
        headers = [],
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        cssClasses = [],
        children
    } = props

    const isFaqPage = cssClasses?.includes('js-faq-page-usa')
    const [scrollDirection, setScrollDirection] = useState()

    const handleMouseDown = useCallback((event) => {
        isScrolling.current = true
        clientX.current = event.clientX
    }, [])

    const handleMouseUp = useCallback(() => {
        isScrolling.current = false
    }, [])

    const handleMouseMove = useCallback(
        (event) => {
            if (isScrolling.current && scrollElement) {
                scrollElement.scrollLeft =
                    scrollX.current + (clientX.current - event.clientX)
                scrollX.current = scrollElement.scrollLeft
                clientX.current = event.clientX
            }
        },
        [scrollElement]
    )

    const handleScroll = useCallback(
        (event) => {
            const navScrollElement = event.target
            if (navScrollElement.scrollLeft > 0) {
                if (
                    navScrollElement.scrollLeft +
                        navScrollElement.offsetWidth +
                        1 >=
                    navScrollElement.scrollWidth
                ) {
                    setGradient('left')
                } else {
                    setGradient('both')
                }
            } else {
                setGradient('right')
            }
        },
        [setGradient]
    )

    useEffect(() => {
        let navScrollElement
        const navigationWrapper = navigationRef.current

        if (
            navigationWrapper &&
            navigationWrapper.childNodes[0].nodeName === 'UL'
        ) {
            navScrollElement = navigationWrapper.childNodes[0]
            setScrollElement(navScrollElement)
            if (navScrollElement.scrollWidth > navScrollElement.offsetWidth) {
                setGradient('right')
            }
            navScrollElement.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (navScrollElement) {
                navScrollElement.removeEventListener('scroll', handleScroll)
            }
        }
    }, [handleScroll])

    const handlePageScroll = throttle(() => {
        const currentScrollY = window.scrollY
        const _scrollDirection =
            currentScrollY > lastScrollY.current ? 'down' : 'up'
        setScrollDirection(_scrollDirection)
        lastScrollY.current = currentScrollY
    }, 200)

    useEffect(() => {
        document.addEventListener('scroll', handlePageScroll)
        return () => {
            document.removeEventListener('scroll', handlePageScroll)
        }
    }, [])

    const handleTabClick = (index) => {
        setSelectedIndex(index)
        if (!index) return
        if (isFaqPage && tabRefs.current[index]) {
            const element = tabRefs.current[index]

            if (element.scrollIntoView) {
                element.scrollIntoView({ behavior: 'smooth' })

                // Adjust scroll position to account for any sticky headers or offsets
                let yOffset = index === 1 ? -450 : -265
                if (index === 2 && scrollDirection === 'down') {
                    yOffset = -450
                }
                const yPosition =
                    element.getBoundingClientRect().top +
                    window.pageYOffset +
                    yOffset

                window.scrollTo({ top: yPosition, behavior: 'smooth' })
            }
        }
    }

    if (!headers.length) {
        return null
    }

    const wrapperStyles = {
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft
    }

    const contentStyles = {
        minHeight,
        textAlign
    }

    const tabWrapperProps = {
        defaultIndex
    }

    if (border) {
        wrapperStyles['--tabs-border'] = border
        wrapperStyles['--tabs-border-color'] = borderColor
    }
    if (borderWidth) {
        wrapperStyles['--tabs-border-width'] = borderWidth
    }
    if (borderRadius) {
        wrapperStyles['--tabs-border-radius'] = borderRadius
    }

    const tabHeaders = Children.map(children, (child, index) => (
        <TabHeader
            key={index}
            onClick={() => handleTabClick(index)}
            className={[child.props.data.cssClasses, classes.header].join(' ')}
            selectedClassName={classes.panelSelected}
        >
            {child.props.data.tabName}
        </TabHeader>
    ))

    const tabPanels = Children.map(children, (child, index) => (
        <div
            key={index}
            ref={(el) => (tabRefs.current[index] = el)} // Store refs for smooth scroll
            className={classes.panel}
            style={
                isFaqPage || selectedIndex === index
                    ? { display: 'block' } // Show all tabs when `isFaqPage` is true, or selected tab when false
                    : { display: 'none' } // Hide unselected tabs when `isFaqPage` is false
            }
        >
            {child}
        </div>
    ))

    const navigationClass =
        classes[`navigation${upperCaseString(tabNavigationAlignment)}`]
    const contentClass =
        classes[`content${upperCaseString(tabNavigationAlignment)}`]

    const navigationWrapperClass = gradient
        ? classes[`navigationGradient${upperCaseString(gradient)}`]
        : null

    return (
        <TabWrapper
            style={wrapperStyles}
            className={[classes.root, ...cssClasses].join(' ')}
            disabledTabClassName={classes.disabled}
            selectedTabClassName={classes.selected}
            {...tabWrapperProps}
        >
            <div className={navigationWrapperClass} ref={navigationRef}>
                <TabList
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseUp}
                    className={navigationClass}
                >
                    {tabHeaders}
                </TabList>
            </div>
            <div className={contentClass} style={contentStyles}>
                {tabPanels}
            </div>
        </TabWrapper>
    )
}

Tabs.propTypes = {
    classes: shape({
        header: string,
        panelSelected: string,
        panel: string,
        contentLeft: string,
        contentCenter: string,
        contentRight: string,
        navigationLeft: string,
        navigationCenter: string,
        navigationRight: string,
        navigationGradientLeft: string,
        navigationGradientRight: string,
        navigationGradientBoth: string,
        disabled: string,
        selected: string,
        item: string
    }),
    tabNavigationAlignment: oneOf(['left', 'center', 'right']),
    minHeight: string,
    defaultIndex: number,
    headers: arrayOf(string),
    textAlign: string,
    border: string,
    borderColor: string,
    borderWidth: string,
    borderRadius: string,
    marginTop: string,
    marginRight: string,
    marginBottom: string,
    marginLeft: string,
    paddingTop: string,
    paddingRight: string,
    paddingBottom: string,
    paddingLeft: string,
    cssClasses: arrayOf(string)
}

export default Tabs
